import { getAuthToken } from './authApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5215';

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handleResponse = async (response) => {
  let data = null;
  try { data = await response.json(); } catch { /* no body */ }
  if (!response.ok) {
    const err = new Error(data?.error || data?.message || `HTTP error! status: ${response.status}`);
    err.status = response.status;
    err.details = data;
    throw err;
  }
  return data;
};

/**
 * Distinct tenant slugs in the prod subscription. Cached server-side.
 * Fast (no settings fetch).
 */
export const listTenants = async () => {
  const res = await fetch(`${API_BASE_URL}/api/envscan/tenants`, {
    headers: { ...authHeaders() },
  });
  return handleResponse(res);
};

/**
 * Service types available for one tenant (e.g. ['scus-app-api', 'scus-app-backoffice']).
 */
export const listServicesForTenant = async (tenantSlug) => {
  const res = await fetch(
    `${API_BASE_URL}/api/envscan/tenant/${encodeURIComponent(tenantSlug)}/services`,
    { headers: { ...authHeaders() } }
  );
  return handleResponse(res);
};

/**
 * Pull settings for ONE App Service. Used by Inspector page.
 * Returns ScannedAppService { name, resourceGroup, tenantName, serviceType, location, settings: [...] }.
 */
export const getTenantServiceSettings = async (tenantSlug, serviceType, showSecrets = false) => {
  const params = new URLSearchParams();
  if (showSecrets) params.set('showSecrets', 'true');
  const url = `${API_BASE_URL}/api/envscan/tenant/${encodeURIComponent(tenantSlug)}/service/${encodeURIComponent(serviceType)}/settings${params.toString() ? '?' + params.toString() : ''}`;
  const res = await fetch(url, { headers: { ...authHeaders() } });
  return handleResponse(res);
};

/**
 * Pull settings for several tenants of the same service type, in parallel server-side.
 * Used by Compare page. Capped at 5 tenants.
 */
export const compareSettings = async (tenantSlugs, serviceType, showSecrets = false) => {
  const params = new URLSearchParams();
  params.set('tenants', tenantSlugs.join(','));
  params.set('service', serviceType);
  if (showSecrets) params.set('showSecrets', 'true');
  const res = await fetch(
    `${API_BASE_URL}/api/envscan/compare?${params.toString()}`,
    { headers: { ...authHeaders() } }
  );
  return handleResponse(res);
};

/**
 * @deprecated Legacy full scan. Kept for backward compat with EnvManager page.
 * New UI work should use listTenants + getTenantServiceSettings + compareSettings.
 *
 * Scan environment variables across all tenant App Services.
 * @param {Object} options
 * @param {string[]|null} options.tenantFilter - optional tenant names to limit scope
 * @param {string[]|null} options.apiFilter - optional service type names to limit scope
 * @param {boolean} options.showSecrets - whether to show unmasked secret values
 * @returns {Promise<Object>} EnvScanResult
 */
export const scanEnvVariables = async (options = {}) => {
  const { tenantFilter, apiFilter, showSecrets = false } = options;

  const hasFilters =
    (tenantFilter && tenantFilter.length > 0) ||
    (apiFilter && apiFilter.length > 0);

  let url;
  let fetchOptions;

  if (hasFilters) {
    // Use POST with body for filtered scans
    url = `${API_BASE_URL}/api/envscan/scan`;
    fetchOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenantFilter: tenantFilter && tenantFilter.length > 0 ? tenantFilter : null,
        apiFilter: apiFilter && apiFilter.length > 0 ? apiFilter : null,
        showSecrets,
      }),
    };
  } else {
    // Use GET for unfiltered scans
    const params = new URLSearchParams();
    if (showSecrets) params.set('showSecrets', 'true');
    url = `${API_BASE_URL}/api/envscan/scan${params.toString() ? '?' + params.toString() : ''}`;
    fetchOptions = { method: 'GET' };
  }

  const response = await fetch(url, fetchOptions);
  const data = await response.json();

  if (!response.ok) {
    const err = new Error(data?.error || `HTTP error! status: ${response.status}`);
    err.status = response.status;
    err.details = data;
    throw err;
  }

  return data;
};
